// Reemplaza el componente ReenvioNormativaDialog.jsx completo
import React, { useState, useEffect, useContext, useCallback} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { 
  obtenerClientesDisponiblesReenvio, 
  generarVistaPreviaReenvio, 
  reenviarNormativa 
} from '../../http/actions/DocumentoAction';
import { NotificacionContext } from '../../../componentes/common/NotificacionContext';

const ReenvioNormativaDialog = ({ 
  open, 
  onClose, 
  normativaId, 
  normativaNombre,
  estadosEnvioClientes = [],
  onReenvioExitoso 
}) => {
  const { mostrarNotificacion } = useContext(NotificacionContext);
  
  // Estados principales
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [mensajePersonalizado, setMensajePersonalizado] = useState('');
  const [asuntoPersonalizado, setAsuntoPersonalizado] = useState('');
  
  // Estados de carga y vistas previas
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isEnviando, setIsEnviando] = useState(false);
  const [vistasPrevias, setVistasPrevias] = useState([]);
  
  // Estados de UI
  const [tabValue, setTabValue] = useState(0);
  const [showPreviews, setShowPreviews] = useState(false);

  const cargarClientesDisponibles = useCallback(async () => {
    setIsLoading(true);
    try {
      // Usar los clientes que ya están disponibles en estadosEnvioClientes
      const clientesConEstado = estadosEnvioClientes
        .filter(estado => estado.fueEnviado)
        .map(estado => ({
          id: estado.clienteId,
          nombre: estado.clienteNombre,
          tipoCorreo: 1 // Valor por defecto
        }));
      
      if (clientesConEstado.length === 0) {
        // Si no hay clientes en estadosEnvioClientes, intentar cargar desde el endpoint
        const clientes = await obtenerClientesDisponiblesReenvio(localStorage.getItem('token'));
        setClientesDisponibles(clientes);
      } else {
        setClientesDisponibles(clientesConEstado);
      }
    } catch (error) {
      console.error("Error al cargar clientes:", error);
      mostrarNotificacion("Error al cargar clientes disponibles", "error");
      
      // Fallback: usar solo los clientes que ya están en estadosEnvioClientes
      const clientesFallback = estadosEnvioClientes
        .filter(estado => estado.fueEnviado)
        .map(estado => ({
          id: estado.clienteId,
          nombre: estado.clienteNombre,
          tipoCorreo: 1
        }));
      setClientesDisponibles(clientesFallback);
    } finally {
      setIsLoading(false);
    }
  }, [estadosEnvioClientes, mostrarNotificacion]);

  // Cargar clientes disponibles al abrir el diálogo
  useEffect(() => {
    if (open && normativaId) {
      cargarClientesDisponibles();
    }
  }, [open, normativaId, cargarClientesDisponibles]);

  // Filtrar clientes que ya recibieron la normativa
  const clientesQueRecibieron = estadosEnvioClientes
    .filter(estado => estado.fueEnviado)
    .map(estado => estado.clienteId);

  const clientesFiltrados = clientesDisponibles.filter(cliente => 
    clientesQueRecibieron.includes(cliente.id)
  );

  const handleSeleccionarCliente = useCallback((clienteId, isSelected) => {
    console.log("Seleccionando cliente:", clienteId, isSelected);
    if (isSelected) {
      setClientesSeleccionados(prev => [...prev, clienteId]);
    } else {
      setClientesSeleccionados(prev => prev.filter(id => id !== clienteId));
    }
  }, []);

  const handleSeleccionarTodos = useCallback((event) => {
    console.log("Seleccionar todos:", event.target.checked);
    if (event.target.checked) {
      setClientesSeleccionados(clientesFiltrados.map(cliente => cliente.id));
    } else {
      setClientesSeleccionados([]);
    }
  }, [clientesFiltrados]);

  const handleGenerarVistaPrevia = useCallback(async () => {
    if (clientesSeleccionados.length === 0) {
      mostrarNotificacion("Debe seleccionar al menos un cliente", "warning");
      return;
    }

    setIsLoadingPreview(true);
    try {
      const data = {
        normativaId: parseInt(normativaId),
        clientesIds: clientesSeleccionados
      };

      const previews = await generarVistaPreviaReenvio(localStorage.getItem('token'), data);
      setVistasPrevias(previews);
      setShowPreviews(true);
      setTabValue(1); // Cambiar a la pestaña de vista previa
      
      mostrarNotificacion(`Vista previa generada para ${previews.length} clientes`, "success");
    } catch (error) {
      console.error("Error al generar vista previa:", error);
      mostrarNotificacion("Error al generar vista previa", "error");
    } finally {
      setIsLoadingPreview(false);
    }
  }, [clientesSeleccionados, normativaId, mostrarNotificacion]);

  const handleCerrar = useCallback(() => {
    setClientesSeleccionados([]);
    setMensajePersonalizado('');
    setAsuntoPersonalizado('');
    setVistasPrevias([]);
    setShowPreviews(false);
    setTabValue(0);
    onClose();
  }, [onClose]);
  
  const handleReenviar = useCallback(async () => {
    console.log("=== INICIANDO REENVÍO ===");
    console.log("Clientes seleccionados:", clientesSeleccionados);
    console.log("Normativa ID:", normativaId);
    
    if (clientesSeleccionados.length === 0) {
      console.log("No hay clientes seleccionados");
      mostrarNotificacion("Debe seleccionar al menos un cliente", "warning");
      return;
    }

    setIsEnviando(true);
    try {
      const data = {
        normativaId: parseInt(normativaId),
        clientesIds: clientesSeleccionados,
        mensajePersonalizado: mensajePersonalizado.trim() || null,
        asuntoPersonalizado: asuntoPersonalizado.trim() || null,
        esReenvio: true
      };

      console.log("Data para reenvío:", data);

      const resultado = await reenviarNormativa(localStorage.getItem('token'), data);
      console.log("Resultado del reenvío:", resultado);
      
      if (resultado && resultado.exitoso) {
        mostrarNotificacion(
          `Reenvío completado: ${resultado.clientesExitosos} exitosos, ${resultado.clientesFallidos} fallidos`,
          "success"
        );
        
        if (onReenvioExitoso) {
          onReenvioExitoso(resultado);
        }
        
        handleCerrar();
      } else {
        console.log("Resultado no exitoso:", resultado?.mensaje);
        mostrarNotificacion(resultado?.mensaje || "Error en el reenvío", "error");
      }
    } catch (error) {
      console.error("Error completo al reenviar:", error);
      mostrarNotificacion(`Error al procesar el reenvío: ${error.message || 'Error desconocido'}`, "error");
    } finally {
      setIsEnviando(false);
    }
  }, [clientesSeleccionados, normativaId, mostrarNotificacion, mensajePersonalizado, asuntoPersonalizado, onReenvioExitoso, handleCerrar]);

  const truncarTexto = (texto, maxLength = 100) => {
    if (!texto) return '';
    return texto.length > maxLength ? texto.substring(0, maxLength) + "..." : texto;
  };

  // Agregar logging para debug
  console.log("=== ESTADO DEL COMPONENTE ===");
  console.log("Open:", open);
  console.log("Clientes disponibles:", clientesDisponibles.length);
  console.log("Clientes filtrados:", clientesFiltrados.length);
  console.log("Clientes seleccionados:", clientesSeleccionados);
  console.log("Estados envío:", estadosEnvioClientes.length);

  return (
    <Dialog
      open={open}
      onClose={handleCerrar}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh' }
      }}
      aria-labelledby="reenvio-dialog-title"
      aria-describedby="reenvio-dialog-description"
    >
      <DialogTitle id="reenvio-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6">
            Reenviar Normativa
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: 1 }}
          id="reenvio-dialog-description"
        >
          {truncarTexto(normativaNombre, 80)}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ px: 3 }}
            aria-label="Pestañas de reenvío"
          >
            <Tab 
              label={`Selección (${clientesSeleccionados.length})`} 
              icon={<SendIcon />} 
              iconPosition="start"
              id="tab-seleccion"
              aria-controls="tabpanel-seleccion"
            />
            <Tab 
              label={`Vista Previa (${vistasPrevias.length})`} 
              icon={<PreviewIcon />} 
              iconPosition="start"
              disabled={!showPreviews}
              id="tab-preview"
              aria-controls="tabpanel-preview"
            />
          </Tabs>
        </Box>

        {/* Pestaña de Selección */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }} role="tabpanel" id="tabpanel-seleccion" aria-labelledby="tab-seleccion">
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando clientes...</Typography>
              </Box>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Información:</strong> Solo se muestran los clientes que ya recibieron esta normativa previamente.
                    El reenvío enviará la normativa nuevamente a los clientes seleccionados.
                  </Typography>
                </Alert>

                {/* Personalización del mensaje */}
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel-mensaje-content"
                    id="panel-mensaje-header"
                  >
                    <Typography variant="subtitle1">
                      Personalizar Mensaje (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Asunto personalizado"
                        value={asuntoPersonalizado}
                        onChange={(e) => setAsuntoPersonalizado(e.target.value)}
                        placeholder="Ej: [URGENTE] Reenvío de Normativa Importante"
                        helperText="Si se deja vacío, se usará el asunto por defecto"
                        inputProps={{
                          'aria-label': 'Asunto personalizado del correo'
                        }}
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Mensaje personalizado"
                        value={mensajePersonalizado}
                        onChange={(e) => setMensajePersonalizado(e.target.value)}
                        placeholder="Ej: Se reenvía esta normativa por solicitud especial del área legal..."
                        helperText="Mensaje que aparecerá al inicio del correo"
                        inputProps={{
                          'aria-label': 'Mensaje personalizado del correo'
                        }}
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Selección de clientes */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      Clientes para Reenvío ({clientesFiltrados.length} disponibles)
                    </Typography>
                    
                    {clientesFiltrados.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              clientesSeleccionados.length === clientesFiltrados.length &&
                              clientesSeleccionados.length > 0
                            }
                            onChange={handleSeleccionarTodos}
                            inputProps={{
                              'aria-label': 'Seleccionar todos los clientes'
                            }}
                          />
                        }
                        label="Seleccionar todos"
                      />
                    )}
                  </Box>

                  {clientesFiltrados.length === 0 ? (
                    <Alert severity="warning">
                      No hay clientes disponibles para reenvío. 
                      Solo se pueden reenviar normativas a clientes que ya la recibieron previamente.
                    </Alert>
                  ) : (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        maxHeight: 300, 
                        overflow: 'auto',
                        p: 1
                      }}
                    >
                      {clientesFiltrados.map((cliente) => {
                        const estadoEnvio = estadosEnvioClientes.find(
                          estado => estado.clienteId === cliente.id
                        );
                        
                        return (
                          <Card
                            key={cliente.id}
                            variant="outlined"
                            sx={{ 
                              mb: 1, 
                              '&:hover': { backgroundColor: 'action.hover' },
                              border: clientesSeleccionados.includes(cliente.id) 
                                ? 2 
                                : 1,
                              borderColor: clientesSeleccionados.includes(cliente.id) 
                                ? 'primary.main' 
                                : 'divider'
                            }}
                          >
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Checkbox
                                  checked={clientesSeleccionados.includes(cliente.id)}
                                  onChange={(e) => handleSeleccionarCliente(
                                    cliente.id, 
                                    e.target.checked
                                  )}
                                  inputProps={{
                                    'aria-label': `Seleccionar cliente ${cliente.nombre}`
                                  }}
                                />
                                
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="medium">
                                    {cliente.nombre}
                                  </Typography>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Chip
                                      size="small"
                                      label={`Tipo ${cliente.tipoCorreo}`}
                                      color="default"
                                      variant="outlined"
                                    />
                                    
                                    {estadoEnvio && (
                                      <>
                                        <Chip
                                          size="small"
                                          label={estadoEnvio.estadoEnvio}
                                          color="success"
                                          variant="filled"
                                        />
                                        
                                        {estadoEnvio.fechaEnvio && (
                                          <Typography variant="caption" color="text.secondary">
                                            Enviado: {new Date(estadoEnvio.fechaEnvio).toLocaleDateString('es-ES')}
                                          </Typography>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Paper>
                  )}
                </Box>

                {/* Botón de vista previa */}
                {clientesSeleccionados.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handleGenerarVistaPrevia}
                      disabled={isLoadingPreview}
                      fullWidth
                    >
                      {isLoadingPreview ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Generando Vista Previa...
                        </>
                      ) : (
                        `Generar Vista Previa (${clientesSeleccionados.length} clientes)`
                      )}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Pestaña de Vista Previa */}
        {tabValue === 1 && showPreviews && (
          <Box sx={{ p: 3 }} role="tabpanel" id="tabpanel-preview" aria-labelledby="tab-preview">
            <Typography variant="h6" gutterBottom>
              Vista Previa del Reenvío
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Revise cómo se verá el correo para cada cliente antes de enviar.
            </Alert>

            {vistasPrevias.map((preview) => (
              <Accordion key={preview.clienteId} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {preview.clienteNombre}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mr: 2 }}>
                      <Chip 
                        size="small" 
                        label={`${preview.correosDestinatarios?.length || 0} emails`}
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        size="small" 
                        label={`${preview.modulosContratados?.length || 0} módulos`}
                        color="secondary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Asunto:</strong> {preview.asunto}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Destinatarios:</strong> {preview.correosDestinatarios?.join(', ') || 'N/A'}
                    </Typography>
                    
                    {preview.modulosContratados && preview.modulosContratados.length > 0 && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Módulos:</strong> {preview.modulosContratados.join(', ')}
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box 
                    sx={{ 
                      border: 1, 
                      borderColor: 'divider', 
                      borderRadius: 1, 
                      p: 2,
                      backgroundColor: 'background.paper',
                      maxHeight: 400,
                      overflow: 'auto'
                    }}
                  >
                    <div 
                      dangerouslySetInnerHTML={{ __html: preview.contenidoHTML }}
                      style={{ 
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '14px',
                        lineHeight: '1.4'
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>

        <Button
          onClick={handleReenviar}
          variant="contained"
          disabled={clientesSeleccionados.length === 0 || isEnviando}
          startIcon={isEnviando ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {isEnviando ? (
            'Enviando...'
          ) : (
            `Reenviar a ${clientesSeleccionados.length} clientes`
          )}
        </Button>
        <Button 
          onClick={handleCerrar}
          color="error"
          variant="contained"
        >
          Cancelar
        </Button>
        
      </DialogActions>
    </Dialog>
  );
};

export default ReenvioNormativaDialog;